import React, { useContext, useEffect } from 'react';
import styles from './EledoUserProfile.module.scss';
import {useForm, FormProvider} from "react-hook-form";
import {showNotyf, useErrorNotyf, useShowMenu} from "hooks";
import {
  AccountGeneralFormModel,
  BillingFormModel,
  OperationalNotificationsFormModel,
  PaymentNotificationsFormModel,
  TableFieldIdValidation,
  TableFieldNumericValueValidation,
  TableFieldStringValueValidation
} from "model";
import ReactTooltip from "react-tooltip";
import {
  EledoMainPageTitle, EledoPendingIcon,
  EledoLoader, EledoError, EledoPendingContent,
  EledoFormGenerator, EledoPropertyList, EledoTabs,
  EledoLabel,
} from "components/library";
import TabSubscription from "./TabSubscription";
import classnames from 'classnames';
import { AuthContext } from 'AuthContext';
import { PlatformContext } from 'PlatformContext';
import { useMutation } from '@apollo/client';
import { GENERATE_NEW_API_KEY } from 'graphql/AuthQueries';
import NotyfContext from 'context/NotyfContext';
import useCurrentProfile from 'graphql/hooks/useCurrentProfile';
import { EledoProfile, UPDATE_CURRENT_PROFILE, UpdateEledoProfileData, UpdateEledoProfileVars } from 'graphql/EledoProfile';
import { EledoTab } from 'components/library/EledoTabs/EledoTabs';

const EledoUserProfile = () => {
  const { loading, error, currentProfile } = useCurrentProfile()
  const [ updateCurrentProfile, { loading: saving, error: savingError } ] = useMutation<UpdateEledoProfileData, UpdateEledoProfileVars>(UPDATE_CURRENT_PROFILE)
  
  const userInfo = currentProfile;
  
  const formHandler = useForm<EledoProfile>({mode: "all"});
  const { formState } = formHandler;
  
  const userChanged = formState.isDirty
  const userValid = formState.isValid

  const platform = useContext(PlatformContext);
  const isEmbedded = platform.isEmbedded;
  
  useErrorNotyf(error || savingError);
  useShowMenu();

  useEffect(() => {
    if(currentProfile !== undefined){
      formHandler.reset()
    }
  }, [formHandler, currentProfile])

  const handleSave = () => {
    if(userValid && userChanged) {
      const profile = formHandler.getValues();

      updateCurrentProfile({ variables: { profile }})
    }
  };

  const renderError = () =>
    <EledoError title={error?.message}
                description={(<React.Fragment>
                  We are sorry &#9785; We could not load your user data. <br/><br/>
                  {(error?.message ? error.message.toLocaleLowerCase() : error) === "network error" && "Please check your internet connection."}
                </React.Fragment>)}/>;

  return (
    <div>
      <div className={styles.EledoUserProfile} data-testid="EledoUserProfile">
        <div className={styles.TopToolbar}>
          <EledoMainPageTitle
            title="Your Profile"
          />
          <div className={classnames("section", "my-section", styles.ButtonDiv)}>
            <span
              data-tip={userValid ? userChanged ? "" : "Your profile is saved" : "Please check the validity of your input data"}
              data-for="save-invalid-tooltip">
              <button className="button w-button"
                      onClick={handleSave}
                      {...(userChanged && userValid ? {} : {"aria-disabled": "true"})}>
                { saving ?
                    <React.Fragment>
                      <EledoPendingIcon/> Saving...
                    </React.Fragment> :
                    <React.Fragment>
                      <i className="icon-floppy-disk"/> Save
                    </React.Fragment>
                }
              </button>
            </span>
          </div>

          <ReactTooltip
            id="save-invalid-tooltip"
            place="bottom"
            type="dark"
            effect="float"
            multiline={true}
          />
        </div>

        <div className="stretch-vertically">
          <FormProvider {...formHandler}>
            <form>
              <EledoTabs id="userProfileTabs">
                <EledoTab label="General" path="">
                  {!error && !loading && <TabGeneral userInfo={userInfo} />}
                </EledoTab>
                <EledoTab label="API" path="api">
                  {!error && !loading && !isEmbedded && <TabAPI/>}
                </EledoTab>
                <EledoTab label="Subscription" path="subscription">
                  <TabSubscription/>
                </EledoTab>
                <EledoTab label="Billing Address" path="billing">
                  {!error && !loading && <TabBilling userInfo={userInfo}/>}
                </EledoTab>
                <EledoTab label="Notifications" path="notifications">
                  {!error && !loading && <TabNotifications userInfo={userInfo}/>}
                </EledoTab>
                <EledoTab label="Account termination" path="termination">
                  {!error && !loading && <TabAccountTermination userInfo={userInfo}/>}
                </EledoTab>
              </EledoTabs>
              { loading && <div className="flex-center"><EledoLoader/></div>}
              { error && renderError() }
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}

const TabGeneral = ({ userInfo }: {userInfo: EledoProfile | undefined}) => {
  const auth = useContext(AuthContext)
  if(userInfo === undefined){
    return (<></>)
  }

  return (
    <div>
      <fieldset>
        <EledoLabel id="login" label="User"/>
        <div>{auth.user?.email}</div>
      </fieldset>
      
      <EledoFormGenerator
        formModel={AccountGeneralFormModel}
        editedDataObject={userInfo}
      />
      <h3>Properties</h3>
      {userInfo?.properties && <EledoPropertyList
        defaultValues={userInfo.properties}
        fields={[
          {id: "key", label: "Property Name", type: "text", validation: TableFieldIdValidation},
          {id: "value", label: "Property Value", type: "text", validation: TableFieldStringValueValidation}
        ]}
        path="properties"
      />}
      <h3>Counters</h3>
      {userInfo?.counters && <EledoPropertyList
        defaultValues={userInfo.counters}
        fields={[
          {id: "key", label: "Counter Name", type: "text", validation: TableFieldIdValidation},
          {id: "value", label: "Counter Value", type: "text", validation: TableFieldNumericValueValidation},
        ]}
        path="counters"
      />}

    </div>
  );
};

const TabAPI = () => {
  const auth = useContext(AuthContext);
  const userData = auth.user;
  const notyf = useContext(NotyfContext);

  const [generateNewApiKey, { loading }] = useMutation(GENERATE_NEW_API_KEY, {
    update(proxy, { data }) {
      if(data){
        auth.newApiKey(data.apiToken);
      }
    },
    onError(error){
      showNotyf(error, notyf, "error");
    }
  });

  return (
    <div>
      { userData &&
        <fieldset>
          <EledoLabel id="apiKey" label="API Key"/>
          <input id="apiKey" style={{cursor: "initial"}}
                 value={auth.apiToken}
                 disabled={true}/>
        </fieldset>
      }
      <button className="button w-button outline"
              onClick={e => {e.preventDefault();generateNewApiKey()}}>
        <EledoPendingContent state={loading}
                             content="Generate new API Key"
                             pendingContent="Generating..."/>
      </button>
    </div>
  );
}

const TabBilling = ({ userInfo }: {userInfo: EledoProfile | undefined}) => {
  return (
    <div>
      {userInfo?.billTo && <EledoFormGenerator
        formModel={BillingFormModel}
        editedDataObject={userInfo}
      />}
    </div>
  );
};

const TabNotifications = ({ userInfo }: {userInfo: EledoProfile | undefined}) => (
  <div>
    <h3>Operational notifications</h3>
    {userInfo?.notifications && <EledoFormGenerator
      formModel={OperationalNotificationsFormModel}
      editedDataObject={userInfo}
    />}
    <h3>Payment notifications</h3>
    {userInfo?.notifications && <EledoFormGenerator
      formModel={PaymentNotificationsFormModel}
      editedDataObject={userInfo}
    />}
  </div>
);

const TabAccountTermination = ({userInfo}: {userInfo: EledoProfile | undefined}) => {
  // const {status, error, closeAccount, cancelCloseAccount}
  //   = useSelector(state => state.user.termination);
  // const dispatch = useDispatch();

  // useEffect(() => {
  //   if(status === null) {
  //     dispatch(wasCloseAccountRequestedAction());
  //   }
  // }, [dispatch, status]);


  // useErrorNotyf(error);
  // useErrorNotyf(closeAccount.error);
  // useErrorNotyf(cancelCloseAccount.error);

  // const handleCloseAccount = e => {
  //   e.preventDefault();
  //   dispatch(closeAccountAction());
  // }

  // const handleCancelTermination = e => {
  //   e.preventDefault();
  //   dispatch(cancelCloseAccountAction());
  // }

  return (
    <div>
      {/* <h3>Account termination</h3>
      <p style={{marginBottom: "1rem"}}>
        { status === true &&
          "Your account termination is being reviewed."}
        { status === false &&
          "You can get your account deleted by requesting its termination. " +
          "Your account will be deleted after a confirmation from the Eledo team."
        }
      </p>
      { status === true &&
        <button
          className="button w-button"
          onClick={e => handleCancelTermination(e)}>
          {cancelCloseAccount.loading}
          <EledoPendingContent state={cancelCloseAccount.loading}
                               content="Cancel Account Termination"
                               pendingContent="Cancelling..."/>
        </button>}
      { status === false &&
        <button
          className="button w-button red"
          onClick={e => handleCloseAccount(e)}>
          <EledoPendingContent state={closeAccount.loading}
                               content="Close my Account"
                               pendingContent="Closing your account..."/>
        </button>
      } */}
    </div>
  );
};

export default EledoUserProfile;

